import React, { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowPathIcon, MapIcon, TableCellsIcon } from "@heroicons/react/24/outline";
import { getErrorMessage } from "@/utils/withErrorHandling";
import {
  PushStationCredential,
  PushStationService,
  SnapshotStation,
} from "@/services/PushStationService";
import { WindStation } from "@/services/WindStationService";
import { paginate, sortRows, SortAccessors, useTableState } from "@/hooks/useTableState";
import Pagination from "@/components/common/Pagination";
import SortableHeader from "@/components/common/SortableHeader";
import StatChip from "@/components/common/StatChip";
import FilterSelect from "@/components/common/FilterSelect";
import SearchInput from "@/components/common/SearchInput";
import TableSkeleton from "@/components/common/TableSkeleton";
import SidePanel, { DetailRow, DetailSection } from "@/components/common/SidePanel";
import {
  FreshnessBadge,
  SourceBadge,
  fmtDir,
  fmtKts,
  fmtLastObs,
} from "./stationDisplay";
import { StationRow, buildStationRows, credentialKeyOf } from "./cloudflareStations";

// Leaflet is ESM-only and needs a real layout engine: load the map only when it is shown,
// which also keeps it out of the Jest module graph.
const WindStationsMap = React.lazy(() => import("@/pages/admin/WindStationsMap"));

/**
 * SRF-2749 / SRF-2813 — every station served by the Cloudflare live-wind plane, as ONE row
 * per physical station (registry ⨝ snapshot, see cloudflareStations.ts).
 *
 * Table state (search, filters, sort, page, page size, view) lives in the URL via
 * useTableState, so a filtered view can be linked and Back works.
 *
 * Default sort: NAME ascending. Predictable and scannable for "find station X"; freshness
 * triage is one click away (the Freshness header, or the stale / no-data chips).
 *
 * Read-only: credential management stays on the station-management page (/push-stations).
 */

const PAGE_SIZES = [25, 50, 100, 250];
const DEFAULT_PAGE_SIZE = 50;

const DEFAULT_FILTERS = {
  source: "all",
  ingest: "all",
  owner: "all",
  type: "all",
  fresh: "all",
  cred: "all",
};
type Filters = typeof DEFAULT_FILTERS;

/** live < stale < no data — ascending freshness sort puts working stations first. */
const FRESH_RANK = { live: 0, stale: 1, nodata: 2 } as const;

const ACCESSORS: SortAccessors<StationRow> = {
  id: (r) => r.id,
  name: (r) => r.name,
  source: (r) => r.source,
  wind: (r) => r.windKts,
  gust: (r) => r.gustKts,
  dir: (r) => r.windDir,
  obs: (r) => r.obsTs,
  fresh: (r) => FRESH_RANK[r.freshness],
};

/**
 * Credential state (SRF-2813). A pull station may ALSO hold an unused push credential — the
 * duplicate this view exists to surface — so the two are reported separately.
 */
const CredentialCell: React.FC<{ row: StationRow }> = ({ row }) => {
  const cred = row.credential;
  if (!cred) return <span className="text-gray-500">—</span>;
  if (row.ingest === "pull") {
    const token = cred.contact?.pollAuthTokenConfigured;
    return (
      <div className="flex flex-col items-start gap-1">
        <span className="rounded px-2 py-0.5 text-xs font-medium bg-purple-500/15 text-purple-300">
          {token === false ? "pull · no token" : "pull config"}
        </span>
        {cred.prefix && (
          <span
            className="rounded px-2 py-0.5 text-xs font-medium bg-gray-700 text-gray-300"
            title="This station also has an unused push credential"
          >
            + unused push cred
          </span>
        )}
      </div>
    );
  }
  if (!cred.prefix) {
    return (
      <span className="rounded px-2 py-0.5 text-xs font-medium bg-gray-700 text-gray-300">
        no credential
      </span>
    );
  }
  return (
    <span
      className={`rounded px-2 py-0.5 text-xs font-medium ${
        cred.status === "active"
          ? "bg-green-500/15 text-green-300"
          : cred.status === "pending"
            ? "bg-yellow-500/15 text-yellow-300"
            : "bg-red-500/15 text-red-300"
      }`}
    >
      {cred.status}
    </span>
  );
};

const matchesQuery = (r: StationRow, q: string): boolean => {
  if (!q) return true;
  const c = r.credential?.contact;
  return [r.id, r.registryId, r.name, r.source, c?.organization, c?.contactName, c?.address]
    .some((v) => (v || "").toLowerCase().includes(q));
};

const toMapStation = (r: StationRow): WindStation => ({
  id: r.key,
  name: r.name,
  lat: r.lat as number,
  lon: r.lon as number,
  source: r.source,
  windKts: r.windKts ?? undefined,
  gustKts: r.gustKts ?? undefined,
  windDir: r.windDir ?? undefined,
  updatedAt: r.obsTs ?? undefined,
  stale: r.freshness !== "live",
});

const PushStationsStatusView: React.FC = () => {
  const [snapshot, setSnapshot] = useState<SnapshotStation[]>([]);
  const [generation, setGeneration] = useState<number | null>(null);
  const [credentials, setCredentials] = useState<PushStationCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const table = useTableState<Filters>({
    defaultFilters: DEFAULT_FILTERS,
    defaultSort: { key: "name", dir: "asc" },
    defaultPageSize: DEFAULT_PAGE_SIZE,
    pageSizes: PAGE_SIZES,
    descFirst: ["wind", "gust", "obs"],
  });
  const { filters, setFilter, query, sort } = table;

  // Table / Map is a view, not a filter: it must not count toward "Clear filters".
  const [params, setParams] = useSearchParams();
  const view: "table" | "map" = params.get("view") === "map" ? "map" : "table";
  const setView = (v: "table" | "map") =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (v === "table") next.delete("view");
        else next.set("view", v);
        return next;
      },
      { replace: true }
    );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    // The registry is the row identity, not just a push-side join, so it is needed in
    // both ingest modes — a pull station's config and ownership only exist there.
    const [snap, creds] = await Promise.allSettled([
      PushStationService.fetchAllCloudflare(),
      PushStationService.fetchAll(true),
    ]);
    if (snap.status === "fulfilled") {
      setSnapshot(snap.value.stations || []);
      setGeneration(snap.value.generation ?? null);
    } else {
      setSnapshot([]);
      setGeneration(null);
      setError(getErrorMessage(snap.reason, "loading Cloudflare snapshot"));
    }
    if (creds.status === "fulfilled") {
      setCredentials(creds.value);
    } else {
      setCredentials([]);
      if (snap.status === "fulfilled") {
        setError(getErrorMessage(creds.reason, "loading push credentials"));
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => buildStationRows(snapshot, credentials), [snapshot, credentials]);

  /** Counts over ALL rows — the chips summarise the plane, they do not shrink with filters. */
  const counts = useMemo(() => {
    const bySource: Record<string, number> = {};
    const c = { push: 0, pull: 0, live: 0, stale: 0, nodata: 0 };
    for (const r of rows) {
      bySource[r.source] = (bySource[r.source] || 0) + 1;
      c[r.ingest]++;
      c[r.freshness]++;
    }
    return { ...c, bySource };
  }, [rows]);

  const sources = useMemo(() => Object.keys(counts.bySource).sort(), [counts]);
  const stationTypes = useMemo(
    () =>
      Array.from(
        new Set(
          rows.map((r) => r.credential?.contact?.stationType).filter((t): t is string => Boolean(t))
        )
      ).sort(),
    [rows]
  );

  const filtered = useMemo(
    () =>
      rows.filter((r) => {
        if (filters.source !== "all" && r.source !== filters.source) return false;
        // Ingest and ownership are INDEPENDENT axes: an owner-registered pull station
        // matches both `pull` and `owner`.
        if (filters.ingest !== "all" && r.ingest !== filters.ingest) return false;
        if (filters.owner !== "all" && r.ownerRegistered !== (filters.owner === "owner")) return false;
        if (filters.type !== "all" && r.credential?.contact?.stationType !== filters.type) return false;
        if (filters.fresh !== "all" && r.freshness !== filters.fresh) return false;
        if (filters.cred !== "all" && credentialKeyOf(r) !== filters.cred) return false;
        return matchesQuery(r, query);
      }),
    [rows, filters, query]
  );

  const sorted = useMemo(
    () => sortRows(filtered, sort, ACCESSORS, (a, b) => a.name.localeCompare(b.name)),
    [filtered, sort]
  );
  const { pageRows, page, totalPages, total } = paginate(sorted, table.page, table.pageSize);
  const mapStations = useMemo(
    () =>
      view === "map"
        ? sorted.filter((r) => Number.isFinite(r.lat) && Number.isFinite(r.lon)).map(toMapStation)
        : [],
    [sorted, view]
  );

  const selected = useMemo(
    () => (selectedKey ? rows.find((r) => r.key === selectedKey) ?? null : null),
    [rows, selectedKey]
  );

  /** A chip toggles its filter: click once to apply, again to clear. */
  const chip = (key: keyof Filters, value: string) => ({
    active: filters[key] === value,
    onClick: () => setFilter(key, filters[key] === value ? "all" : value),
  });

  const colSpan = 9;
  const filterActive = table.activeFilterCount > 0;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Live stations</h1>
          <p className="mt-1 text-sm text-gray-400">
            Every station served by the Cloudflare live-wind plane — one row per physical
            station.
            {generation != null && <span className="text-gray-500"> Snapshot generation {generation}.</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-md border border-gray-700" role="group" aria-label="View">
            {(
              [
                ["table", "Table", TableCellsIcon],
                ["map", "Map", MapIcon],
              ] as const
            ).map(([v, label, Icon], i) => (
              <button
                key={v}
                type="button"
                aria-pressed={view === v}
                onClick={() => setView(v)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm ${i ? "border-l border-gray-700" : ""} ${
                  view === v ? "bg-gray-700 text-white" : "bg-gray-800 text-gray-400 hover:text-gray-200"
                }`}
              >
                <Icon className="h-4 w-4" aria-hidden="true" /> {label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-md bg-gray-700 px-3 py-2 text-sm text-white hover:bg-gray-600 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
            Refresh
          </button>
          <Link
            to="/push-stations"
            className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-500"
          >
            Manage stations →
          </Link>
        </div>
      </div>

      {/* Summary chips — each one is a quick filter (click again to clear). */}
      <div className="mb-4 flex flex-wrap items-center gap-2" aria-label="Station counts">
        <StatChip
          label="Total"
          count={rows.length}
          active={!filterActive}
          onClick={table.resetFilters}
          title="Clear all filters"
        />
        {sources.map((s) => (
          <StatChip key={s} label={<SourceBadge source={s} />} count={counts.bySource[s]} {...chip("source", s)} />
        ))}
        <span className="mx-1 h-5 w-px bg-gray-700" aria-hidden="true" />
        <StatChip label="push" count={counts.push} {...chip("ingest", "push")} />
        <StatChip label="pull" count={counts.pull} {...chip("ingest", "pull")} />
        <span className="mx-1 h-5 w-px bg-gray-700" aria-hidden="true" />
        <StatChip label="live" tone="ok" count={counts.live} {...chip("fresh", "live")} />
        <StatChip label="stale" tone="warn" count={counts.stale} {...chip("fresh", "stale")} />
        <StatChip label="no data" tone="danger" count={counts.nodata} {...chip("fresh", "nodata")} />
      </div>

      {/* Filter bar. Ingest = how data arrives; Owner = who registered it — independent. */}
      <div className="mb-4 grid grid-cols-2 gap-3 rounded-lg border border-gray-700 bg-gray-800/40 p-3 md:grid-cols-4 xl:grid-cols-8">
        <SearchInput
          className="col-span-2 md:col-span-4 xl:col-span-2"
          label="Search"
          value={table.queryInput}
          onChange={table.setQueryInput}
          placeholder="Id, name, source, organization, address…"
        />
        <FilterSelect
          label="Source"
          value={filters.source}
          onChange={(v) => setFilter("source", v)}
          options={[{ value: "all", label: "All sources" }, ...sources.map((s) => ({ value: s, label: s }))]}
        />
        <FilterSelect
          label="Ingest"
          value={filters.ingest}
          onChange={(v) => setFilter("ingest", v)}
          options={[
            { value: "all", label: "All" },
            { value: "push", label: "Push" },
            { value: "pull", label: "Pull" },
          ]}
        />
        <FilterSelect
          label="Owner"
          value={filters.owner}
          onChange={(v) => setFilter("owner", v)}
          options={[
            { value: "all", label: "All" },
            { value: "owner", label: "Owner-registered" },
            { value: "ops", label: "Ops-managed" },
          ]}
        />
        <FilterSelect
          label="Type"
          value={filters.type}
          onChange={(v) => setFilter("type", v)}
          options={[{ value: "all", label: "All types" }, ...stationTypes.map((t) => ({ value: t, label: t }))]}
        />
        <FilterSelect
          label="Freshness"
          value={filters.fresh}
          onChange={(v) => setFilter("fresh", v)}
          options={[
            { value: "all", label: "All" },
            { value: "live", label: "Live" },
            { value: "stale", label: "Stale" },
            { value: "nodata", label: "No data" },
          ]}
        />
        <FilterSelect
          label="Credential"
          value={filters.cred}
          onChange={(v) => setFilter("cred", v)}
          options={[
            { value: "all", label: "All" },
            { value: "active", label: "Push · active" },
            { value: "pending", label: "Push · pending" },
            { value: "revoked", label: "Push · revoked" },
            { value: "pull", label: "Pull config" },
            { value: "none", label: "None" },
          ]}
        />
      </div>

      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-300">
        <span aria-live="polite">
          {loading
            ? "Loading stations…"
            : `${total.toLocaleString()} of ${rows.length.toLocaleString()} stations`}
        </span>
        {filterActive && (
          <button
            type="button"
            onClick={table.resetFilters}
            className="text-cyan-400 hover:text-cyan-300"
          >
            Clear filters ({table.activeFilterCount})
          </button>
        )}
      </div>

      {error && (
        <div role="alert" className="mb-4 rounded border border-red-700 bg-red-900/40 p-3 text-red-200">
          {error}
        </div>
      )}

      {view === "map" ? (
        <>
          <Suspense
            fallback={
              <div className="flex h-[70vh] items-center justify-center rounded-lg border border-gray-700 text-gray-400">
                Loading map…
              </div>
            }
          >
            <WindStationsMap
              stations={mapStations}
              selectedId={selectedKey}
              onSelect={(s) => setSelectedKey(s.id)}
            />
          </Suspense>
          <p className="mt-2 text-xs text-gray-400">
            {mapStations.length.toLocaleString()} stations on the map (all current filters
            apply). Arrow points where the wind blows; number is knots. Gray = stale / no data.
          </p>
        </>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-700">
            <table className="min-w-full divide-y divide-gray-700 bg-gray-800/60 text-sm">
              <thead className="bg-gray-900">
                <tr>
                  <SortableHeader label="Id" sortKey="id" sort={sort} onSort={table.toggleSort} />
                  <SortableHeader label="Name" sortKey="name" sort={sort} onSort={table.toggleSort} />
                  <SortableHeader label="Source" sortKey="source" sort={sort} onSort={table.toggleSort} />
                  <SortableHeader label="Wind (kts)" sortKey="wind" sort={sort} onSort={table.toggleSort} align="right" />
                  <SortableHeader label="Gust (kts)" sortKey="gust" sort={sort} onSort={table.toggleSort} align="right" />
                  <SortableHeader label="Dir" sortKey="dir" sort={sort} onSort={table.toggleSort} align="right" />
                  <SortableHeader label="Last obs" sortKey="obs" sort={sort} onSort={table.toggleSort} />
                  <SortableHeader label="Freshness" sortKey="fresh" sort={sort} onSort={table.toggleSort} />
                  <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-300">
                    Credential
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/70 text-gray-200">
                {loading ? (
                  <TableSkeleton cols={colSpan} />
                ) : pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={colSpan} className="px-4 py-10 text-center text-gray-400">
                      {rows.length === 0 ? (
                        "No stations in the Cloudflare snapshot yet."
                      ) : (
                        <>
                          No stations match the filters.{" "}
                          <button type="button" onClick={table.resetFilters} className="text-cyan-400 hover:text-cyan-300">
                            Clear filters
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ) : (
                  pageRows.map((r) => (
                    <tr
                      key={r.key}
                      onClick={() => setSelectedKey(r.key)}
                      className={`cursor-pointer hover:bg-gray-700/40 ${selectedKey === r.key ? "bg-gray-700/60" : ""}`}
                    >
                      <td className="px-3 py-2 font-mono text-xs text-gray-300">{r.id}</td>
                      <td className="px-3 py-2 text-white">
                        {/* The name is the keyboard target for opening the detail panel. */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedKey(r.key);
                          }}
                          className="text-left hover:text-cyan-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 rounded"
                        >
                          {r.name}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <SourceBadge source={r.source} />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-white">{fmtKts(r.windKts)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtKts(r.gustKts)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtDir(r.windDir)}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-300">{fmtLastObs(r.obsTs)}</td>
                      <td className="px-3 py-2">
                        <FreshnessBadge freshness={r.freshness} />
                      </td>
                      <td className="px-3 py-2">
                        <CredentialCell row={r} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!loading && total > 0 && (
            <Pagination
              darkMode
              currentPage={page}
              totalPages={totalPages}
              totalItems={total}
              limit={table.pageSize}
              limitOptions={PAGE_SIZES}
              onPageChange={table.setPage}
              onLimitChange={table.setPageSize}
            />
          )}
        </>
      )}

      {selected && (
        <StationDetailPanel row={selected} onClose={() => setSelectedKey(null)} />
      )}
    </div>
  );
};

const StationDetailPanel: React.FC<{ row: StationRow; onClose: () => void }> = ({ row, onClose }) => {
  const c = row.credential?.contact;
  return (
    <SidePanel
      title={row.name}
      subtitle={row.id}
      onClose={onClose}
      footer={
        row.registryId ? (
          <Link
            to={`/push-stations?view=all&q=${encodeURIComponent(row.registryId)}`}
            className="block w-full rounded-md bg-blue-600 px-3 py-2 text-center text-sm font-medium text-white hover:bg-blue-500"
          >
            Manage this station →
          </Link>
        ) : (
          <p className="text-xs text-gray-400">
            Operated by the {row.source} ingest — nothing to manage per station.
          </p>
        )
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <SourceBadge source={row.source} />
        <FreshnessBadge freshness={row.freshness} />
        <span className="text-xs text-gray-400">{row.ingest}</span>
        {row.ownerRegistered && <span className="text-xs text-gray-400">· owner-registered</span>}
      </div>
      <DetailSection title="Current observation">
        <DetailRow label="Wind">{row.windKts != null ? `${fmtKts(row.windKts)} kts` : "—"}</DetailRow>
        <DetailRow label="Gust">{row.gustKts != null ? `${fmtKts(row.gustKts)} kts` : "—"}</DetailRow>
        <DetailRow label="Direction">{fmtDir(row.windDir)}</DetailRow>
        <DetailRow label="Last obs">
          {fmtLastObs(row.obsTs)}
          {row.obsTs ? <div className="text-xs text-gray-400">{new Date(row.obsTs).toISOString().replace("T", " ").slice(0, 16)} UTC</div> : null}
        </DetailRow>
        <DetailRow label="Coordinates">
          {row.lat != null && row.lon != null ? `${row.lat.toFixed(4)}, ${row.lon.toFixed(4)}` : "—"}
        </DetailRow>
      </DetailSection>
      {row.credential && (
        <DetailSection title="Registry">
          <DetailRow label="Station id">{row.registryId}</DetailRow>
          <DetailRow label="Status">{row.credential.status}</DetailRow>
          <DetailRow label="Credential">
            <CredentialCell row={row} />
          </DetailRow>
          {c?.stationType && <DetailRow label="Type">{c.stationType}</DetailRow>}
          {c?.organization && <DetailRow label="Organization">{c.organization}</DetailRow>}
          {c?.pollProvider && (
            <DetailRow label="Pull">
              {c.pollProvider} · every {c.pollIntervalS ?? "?"}s
            </DetailRow>
          )}
        </DetailSection>
      )}
    </SidePanel>
  );
};

export default PushStationsStatusView;
